import React, { useCallback, useMemo } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import {
  CommunicationContextType,
  ComposeContextRowType
} from '../../../lib/types/communication.types';
import { CONTEXT_ROW_TYPES } from './context-selector.constants';
import { buildRowFromOption, isValueKind } from './context-selector.helpers';
import { getContextTypeMessages } from './context-selector.messages';
import { IContextOption, IContextRowProps } from './context-selector.types';
import { ContextTypeSelect } from './context-type-select.component';
import { ContextRowActions } from './context-row-actions.component';
import { ContextRowReadonly } from './context-row-readonly.component';
import { renderFlaggedTags } from './context-row-tags';
import { RowGrid } from './context-selector.styles';

// The ONE row implementation: type menu, single/multi picker, chip flags,
// lock and remove. Data-source feeders (preloaded/paginated) wrap and feed it.
export const ContextRow: React.FC<IContextRowProps> = ({
  type,
  selections,
  contexts,
  readOnly,
  options: feedOptions,
  loading,
  flaggedKeys,
  flagTitle,
  helper,
  onSearchInput,
  onSelectionsChange,
  onTypeChange,
  onEdit,
  onApprove,
  onCancel,
  onRemove
}) => {
  const termLookup = useCompanySportTerminology();
  const typeMessages = getContextTypeMessages(termLookup);

  // The wire's AudienceCriteria carries a single sessionId, so the session row
  // is the one single-select row; every other kind multi-selects.
  const isSingle = type === CommunicationContextType.ASSESSMENT_SESSION;

  // One row per kind — kinds already present elsewhere leave this type menu.
  const disabledTypes = useMemo(
    () => CONTEXT_ROW_TYPES.filter(
      (candidate) => candidate !== type && contexts.some((row) => row.type === candidate)
    ),
    [contexts, type]
  );

  // Chips render from the stored selections so they survive instance deletion.
  const selectedOptions = useMemo(
    () => selections.map((row) => ({
      key: (isValueKind(row.type) ? row.value : row.id) as string,
      label: row.label
    })),
    [selections]
  );

  // Selected options join the list so chips stay valid values even when the
  // feeder's list omits them (deleted, scoped out, or a later search page).
  const options = useMemo(() => {
    const missingSelected = selectedOptions.filter(
      (selected) => !feedOptions.some((option) => option.key === selected.key)
    );
    return [...missingSelected, ...feedOptions];
  }, [selectedOptions, feedOptions]);

  const handleTypeChange = useCallback(
    (to: ComposeContextRowType) => onTypeChange(type, to),
    [type, onTypeChange]
  );

  const handleMultiChange = useCallback((
    _event: React.ChangeEvent<{}>,
    picked: IContextOption[]
  ) => {
    onSelectionsChange(type, picked.map((option) => buildRowFromOption(type, option)));
  }, [type, onSelectionsChange]);

  const handleSingleChange = useCallback((
    _event: React.ChangeEvent<{}>,
    option: IContextOption | null
  ) => {
    onSelectionsChange(type, option ? [buildRowFromOption(type, option)] : []);
  }, [type, onSelectionsChange]);

  const handleInputChange = useCallback((
    _event: React.ChangeEvent<{}>,
    value: string,
    reason: string
  ) => {
    // 'input' only — selection/clear resets would clobber the live search text.
    if (reason === 'input') onSearchInput?.(value);
  }, [onSearchInput]);

  const handleEdit = useCallback(() => onEdit(type), [type, onEdit]);
  const handleApprove = useCallback(() => onApprove(type), [type, onApprove]);
  const handleCancel = useCallback(() => onCancel(type), [type, onCancel]);
  const handleRemove = useCallback(() => onRemove(type), [type, onRemove]);

  if (readOnly) {
    return (
      <ContextRowReadonly
        typeMessage={typeMessages[type]}
        chips={selectedOptions.map((option) => ({
          key: option.key,
          label: option.label,
          flagged: flaggedKeys.has(option.key),
          flagTitle
        }))}
        helper={helper}
        onEdit={handleEdit}
        onRemove={handleRemove}
      />
    );
  }

  return (
    <RowGrid>
      <ContextTypeSelect value={type} onChange={handleTypeChange} disabledTypes={disabledTypes} />

      {isSingle ? (
        <Autocomplete<IContextOption>
          className="context-row-instance"
          size="small"
          options={options}
          value={selectedOptions[0] || null}
          loading={loading}
          getOptionLabel={(option) => option.label}
          getOptionSelected={(option, value) => option.key === value.key}
          onChange={handleSingleChange}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              error={flaggedKeys.size > 0}
              helperText={helper}
              placeholder="Select…"
              inputProps={{ ...params.inputProps, 'aria-label': 'Context instance' }}
            />
          )}
        />
      ) : (
        <Autocomplete<IContextOption, true, false, false>
          className="context-row-instance"
          multiple
          size="small"
          options={options}
          value={selectedOptions}
          loading={loading}
          filterSelectedOptions
          getOptionLabel={(option) => option.label}
          getOptionSelected={(option, value) => option.key === value.key}
          onChange={handleMultiChange}
          onInputChange={onSearchInput ? handleInputChange : undefined}
          // Server-searched feeders filter upstream; the default client filter
          // would re-filter fresh results against the stale debounced input.
          filterOptions={onSearchInput ? (unfiltered) => unfiltered : undefined}
          renderTags={renderFlaggedTags(flaggedKeys, flagTitle)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              helperText={helper}
              placeholder="Select…"
              inputProps={{ ...params.inputProps, 'aria-label': 'Context instance' }}
            />
          )}
        />
      )}

      <ContextRowActions onApprove={handleApprove} onCancel={handleCancel} />
    </RowGrid>
  );
};
